import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2, Route, ScrollText } from 'lucide-react';
import { type UIEvent, useState } from 'react';

import { useAuditoriaAdminControllerListar } from '@/api/generated/admin-auditoria/admin-auditoria';
import { useAmbientesControllerListar } from '@/api/generated/admin-ambientes/admin-ambientes';
import {
  getUsuariosControllerListarQueryKey,
  usuariosControllerListar,
} from '@/api/generated/admin-usuarios/admin-usuarios';
import type { AuditoriaResponse, AuditoriaResponseAcao } from '@/api/generated/model';
import { AlteracoesDialogo } from '@/features/admin/auditoria/components/AlteracoesDialogo';
import { FunilDeDiscussoesTab } from '@/features/admin/auditoria/components/FunilDeDiscussoesTab';
import {
  ACOES,
  CLASSE_ACAO,
  ENTIDADES,
  fimDoDiaISO,
  inicioDoDiaISO,
  lerDiff,
  ROTULO_ACAO,
  rotuloDaEntidade,
} from '@/features/admin/auditoria/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';

const TODOS = 'todos';

/**
 * Trilha de auditoria.
 *
 * ⚠️ Read-only, e é o ponto: não há criar, editar nem excluir aqui porque não
 * há no servidor. Por isso esta tela foge do molde das outras de administração
 * — sem botão de "novo", sem menu de ações, sem AlertDialog de confirmação e
 * sem mutação nenhuma para invalidar. O único caminho de escrita para a tabela
 * `audit_log` é o interceptor, disparado pelas outras telas.
 *
 * ⚠️ Sem campo de busca de propósito. O `busca` entra no schema de toda
 * listagem paginada, mas o `AuditoriaService.listar` não o usa — um input aqui
 * não filtraria nada e pareceria quebrado. Filtra-se por campo.
 */
export function AuditoriaPage() {
  const { pode } = usePermissoes();

  const [aba, setAba] = useState<'trilha' | 'funil'>('trilha');
  const [pagina, setPagina] = useState(1);
  const [acao, setAcao] = useState<string>(TODOS);
  const [entidade, setEntidade] = useState<string>(TODOS);
  const [ambienteId, setAmbienteId] = useState<string>(TODOS);
  const [autorId, setAutorId] = useState<string>(TODOS);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [detalhe, setDetalhe] = useState<AuditoriaResponse | null>(null);

  const { data, isPending, isError, refetch } = useAuditoriaAdminControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'criadoEm',
    // Explícito porque o default do schema de paginação é `asc` — sem isto a
    // trilha abriria no registro mais ANTIGO, que é o avesso do que se procura.
    ordem: 'desc',
    ...(acao !== TODOS ? { acao: acao as AuditoriaResponseAcao } : {}),
    ...(entidade !== TODOS ? { entidade } : {}),
    ...(ambienteId !== TODOS ? { tenantId: ambienteId } : {}),
    ...(autorId !== TODOS ? { autorId } : {}),
    ...(de ? { de: inicioDoDiaISO(de) } : {}),
    ...(ate ? { ate: fimDoDiaISO(ate) } : {}),
  });

  // Servem a dois propósitos: o <Select> de filtro e o mapa que resolve o id
  // que vem em cada linha para um nome. A resposta da auditoria traz só ids.
  //
  // O `enabled` evita 403 para quem tem a chave da auditoria mas não a de
  // ambientes/usuários — o erro apareceria numa tela que não tem relação com o
  // que a pessoa está fazendo.
  const { data: ambientes } = useAmbientesControllerListar(
    { pagina: 1, porPagina: 100, ordenarPor: 'nome', ordem: 'asc' },
    { query: { enabled: pode('admin_tenants.visualizar') } },
  );

  // Paginado sob demanda: o teto do servidor é 100 por página, e com mais
  // usuários que isso quem vinha depois do "P" nunca aparecia. A próxima página
  // é buscada quando a lista aberta chega perto do fim (`aoRolarAutores`).
  //
  // ⚠️ Chave própria (sufixo `infinito`): o cache do `useUsuariosControllerListar`
  // usa a mesma URL com outro formato de dado (uma página, não `pages[]`).
  const parametrosAutores = { porPagina: 100, ordenarPor: 'nomeCompleto', ordem: 'asc' } as const;
  const {
    data: paginasDeAutores,
    hasNextPage: haMaisAutores,
    fetchNextPage: buscarMaisAutores,
    isFetchingNextPage: buscandoMaisAutores,
  } = useInfiniteQuery({
    queryKey: [...getUsuariosControllerListarQueryKey(parametrosAutores), 'infinito'],
    queryFn: ({ pageParam, signal }) =>
      usuariosControllerListar({ ...parametrosAutores, pagina: pageParam }, { signal }),
    initialPageParam: 1,
    getNextPageParam: (ultima) =>
      ultima.pagina < ultima.totalPaginas ? ultima.pagina + 1 : undefined,
    enabled: pode('admin_usuarios.visualizar'),
  });
  const autores = paginasDeAutores?.pages.flatMap((p) => p.itens);

  // O scroll acontece no Viewport interno do Radix, que o `SelectContent` não
  // expõe — e `scroll` não borbulha, por isso a fase de captura.
  function aoRolarAutores(e: UIEvent<HTMLElement>) {
    const alvo = e.target as HTMLElement;
    const pertoDoFim = alvo.scrollHeight - alvo.scrollTop - alvo.clientHeight < 80;
    if (pertoDoFim && haMaisAutores && !buscandoMaisAutores) void buscarMaisAutores();
  }

  const nomeDoAmbiente = new Map(ambientes?.itens.map((a) => [a.id, a.nome]));

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  return (
    <div className="space-y-6">
      <Tabs value={aba} onValueChange={(v) => setAba(v as 'trilha' | 'funil')}>
        <TabsList>
          <TabsTrigger value="trilha">
            <ScrollText className="w-4 h-4" />
            Trilha de auditoria
          </TabsTrigger>
          <TabsTrigger value="funil">
            <Route className="w-4 h-4" />
            Funil de discussões
          </TabsTrigger>
        </TabsList>

        <TabsContent value="trilha" className="mt-4 space-y-6">
      <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3">
        <Select value={acao} onValueChange={(v) => aoFiltrar(() => setAcao(v))}>
          <SelectTrigger className="lg:w-44" aria-label="Filtrar por ação">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todas as ações</SelectItem>
            {ACOES.map((valor) => (
              <SelectItem key={valor} value={valor}>
                {ROTULO_ACAO[valor]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={entidade} onValueChange={(v) => aoFiltrar(() => setEntidade(v))}>
          <SelectTrigger className="lg:w-52" aria-label="Filtrar por entidade">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todas as entidades</SelectItem>
            {ENTIDADES.map((valor) => (
              <SelectItem key={valor} value={valor}>
                {rotuloDaEntidade(valor)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sem opção "Globais": o service só aplica o filtro quando `tenantId`
            vem preenchido, então pedir os globais seria um no-op silencioso. */}
        {ambientes && (
          <Select value={ambienteId} onValueChange={(v) => aoFiltrar(() => setAmbienteId(v))}>
            <SelectTrigger className="lg:w-52" aria-label="Filtrar por ambiente">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os ambientes</SelectItem>
              {ambientes.itens.map((ambiente) => (
                <SelectItem key={ambiente.id} value={ambiente.id}>
                  {ambiente.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {autores && (
          <Select value={autorId} onValueChange={(v) => aoFiltrar(() => setAutorId(v))}>
            <SelectTrigger className="lg:w-52" aria-label="Filtrar por autor">
              <SelectValue />
            </SelectTrigger>
            <SelectContent onScrollCapture={aoRolarAutores}>
              <SelectItem value={TODOS}>Todos os autores</SelectItem>
              {autores.map((autor) => (
                <SelectItem key={autor.id} value={autor.id}>
                  {autor.nomeCompleto}
                </SelectItem>
              ))}
              {buscandoMaisAutores && (
                <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Carregando mais autores…
                </div>
              )}
            </SelectContent>
          </Select>
        )}

        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={de}
            onChange={(e) => aoFiltrar(() => setDe(e.target.value))}
            aria-label="Data inicial"
            className="w-40"
          />
          <span className="text-muted-foreground text-sm">até</span>
          <Input
            type="date"
            value={ate}
            onChange={(e) => aoFiltrar(() => setAte(e.target.value))}
            aria-label="Data final"
            className="w-40"
          />
        </div>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Trilha de auditoria
          </h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={ScrollText}
            titulo="Nenhum registro"
            descricao="Nenhuma ação bate com os filtros escolhidos."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quando</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Entidade</TableHead>
                    <TableHead>Autor</TableHead>
                    <TableHead>Ambiente</TableHead>
                    <TableHead>Alterações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((registro) => {
                    const diff = lerDiff(registro.alteracoes);
                    const campos = diff ? Object.keys(diff).length : 0;

                    return (
                      <TableRow key={registro.id}>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {new Date(registro.criadoEm).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={CLASSE_ACAO[registro.acao]}>
                            {ROTULO_ACAO[registro.acao]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-foreground">
                            {rotuloDaEntidade(registro.entidade)}
                          </span>
                          <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                            {registro.entidadeId.slice(0, 8)}
                          </p>
                        </TableCell>
                        <TableCell>
                          {/* `autorId` é ON DELETE SET NULL: autor removido do
                              banco deixa a linha da trilha em pé, sem nome. */}
                          <span className="text-foreground">{registro.autorNome ?? 'Sistema'}</span>
                        </TableCell>
                        <TableCell>
                          <AmbienteDaLinha
                            tenantId={registro.tenantId}
                            nome={
                              registro.tenantId ? nomeDoAmbiente.get(registro.tenantId) : undefined
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {diff ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDetalhe(registro)}
                              aria-label={`Ver alterações de ${rotuloDaEntidade(registro.entidade)} em ${new Date(registro.criadoEm).toLocaleString('pt-BR')}`}
                            >
                              {campos === 1 ? '1 campo' : `${campos} campos`}
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>
        </TabsContent>

        <TabsContent value="funil" className="mt-4">
          <FunilDeDiscussoesTab />
        </TabsContent>
      </Tabs>

      {detalhe && (
        <AlteracoesDialogo
          aberto
          aoFechar={() => setDetalhe(null)}
          registro={detalhe}
          ambienteNome={detalhe.tenantId ? nomeDoAmbiente.get(detalhe.tenantId) : undefined}
        />
      )}
    </div>
  );
}

/**
 * `tenantId` nulo é entidade global (cargo, cupom, catálogo) — não é dado
 * faltando. Id sem nome acontece com quem não tem `admin_tenants.visualizar`:
 * mostra o prefixo do uuid em vez de deixar a célula vazia.
 */
function AmbienteDaLinha({ tenantId, nome }: { tenantId: string | null; nome?: string }) {
  if (!tenantId) return <Badge variant="outline">Global</Badge>;
  if (nome) return <span className="text-foreground">{nome}</span>;
  return <span className="text-muted-foreground font-mono text-xs">{tenantId.slice(0, 8)}</span>;
}
